// Журнал рекламы для проверки с телефона, где консоль недоступна.
//
// Пишется всё время, в том числе пока мини-апп свёрнут, — именно ради этого он и
// нужен: посмотреть постфактум, дошёл ли показ до конца, пока экран был скрыт.

export type AdLogLevel = 'info' | 'ok' | 'warn' | 'error';

export interface AdLogEntry {
  at: number;
  level: AdLogLevel;
  tag: string;
  message: string;
  data?: Record<string, unknown>;
}

const MAX_ENTRIES = 300;

const entries: AdLogEntry[] = [];
let listeners: Array<() => void> = [];

export function logAd(
  level: AdLogLevel,
  tag: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  entries.push({ at: Date.now(), level, tag, message, data });
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
  for (const listener of listeners.slice()) {
    try {
      listener();
    } catch {
      // Слушатель панели не должен ронять показ рекламы.
    }
  }
}

export function getAdLog(): AdLogEntry[] {
  return entries.slice();
}

export function clearAdLog(): void {
  entries.length = 0;
  for (const listener of listeners.slice()) listener();
}

export function subscribeAdLog(listener: () => void): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((item) => item !== listener);
  };
}

/** Текущее состояние окружения: подменена ли видимость и геометрия. */
export function adEnvSnapshot(): Record<string, unknown> {
  const win = window as any;
  const snapshot: Record<string, unknown> = {
    platform: String(win.Telegram?.WebApp?.platform || 'unknown'),
    compatInstalled: Boolean(win.__mobileActiveCompatInstalled),
    visibilityState: document.visibilityState,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    adsgramSdk: typeof win.Adsgram?.init === 'function',
  };

  if (typeof win.__forcedActive === 'function') {
    try {
      const forced = win.__forcedActive();
      snapshot.exposedActive = forced?.exposedActive;
      snapshot.realActive = forced?.realActive;
      snapshot.nativeVisibility = forced?.native?.visibilityState;
      snapshot.nativeViewport = `${forced?.native?.innerWidth}x${forced?.native?.innerHeight}`;
      snapshot.viewportFrozen = forced?.viewportFrozen;
      if (forced?.patched) {
        snapshot.patched = Object.keys(forced.patched)
          .filter((key) => forced.patched[key])
          .join(',') || 'нет';
      }
      snapshot.observedTargets = forced?.observedTargets;
      const container = document.getElementById('48373');
      snapshot.shadowOpen = Boolean((container as any)?.shadowRoot);
    } catch {
      snapshot.forcedActiveError = true;
    }
  }

  return snapshot;
}

/**
 * Ловит заморозку вебвью. Тикаем часто; если между тиками прошло заметно больше
 * положенного — значит JS не выполнялся, и остановка отсчёта рекламы происходит
 * не в логике SDK, а на уровне системы. Это единственное, что нельзя починить
 * из страницы, поэтому важно отличить одно от другого.
 */
export function startFreezeWatch(): () => void {
  const TICK_MS = 500;
  const FREEZE_THRESHOLD_MS = 1_500;

  if (typeof window.setInterval !== 'function') return () => {};

  let last = Date.now();
  let frozenTotal = 0;

  const intervalId = window.setInterval(() => {
    const now = Date.now();
    const gap = now - last;
    last = now;
    if (gap < FREEZE_THRESHOLD_MS) return;

    frozenTotal += gap;
    logAd('warn', 'freeze', `JS не выполнялся ${(gap / 1000).toFixed(1)}с`, {
      всегоЗаморожено: `${(frozenTotal / 1000).toFixed(1)}с`,
      ...adEnvSnapshot(),
    });
  }, TICK_MS);

  return () => window.clearInterval(intervalId);
}

function formatTime(at: number): string {
  const d = new Date(at);
  const pad = (n: number, size = 2) => String(n).padStart(size, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

export function formatAdLog(): string {
  const header = Object.entries(adEnvSnapshot())
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join('\n');

  const body = entries
    .map((entry) => {
      const data = entry.data && Object.keys(entry.data).length
        ? ` ${JSON.stringify(entry.data)}`
        : '';
      return `${formatTime(entry.at)} [${entry.level}] ${entry.tag}: ${entry.message}${data}`;
    })
    .join('\n');

  return `=== ОКРУЖЕНИЕ ===\n${header}\n\n=== СОБЫТИЯ (${entries.length}) ===\n${body}`;
}

export { formatTime };
